// ABOUTME: Manual enrollment tab for admin email campaign management
// ABOUTME: Allows admin to manually enroll families in email campaigns
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Grid,
} from '@mui/material';
import axios from 'axios';
import { getCurrentAccessToken } from '../../services/api';

interface FamilyOption {
  id: number;
  name: string;
}

interface CampaignOption {
  id: number;
  name: string;
}

interface ScheduledEmailSummary {
  scheduledEmailId: string;
  stepName: string;
  scheduledTimeInFamilyTimezone: string;
}

interface EnrollmentResult {
  success: boolean;
  message: string;
  enrollmentId: number;
  scheduledEmails: ScheduledEmailSummary[];
}

interface RecentEnrollment {
  id: number;
  familyName: string;
  campaignName: string;
  enrolledAt: string;
  triggerReason: string;
  status: string;
  scheduledEmailsCount: number;
}

interface ManualEnrollmentTabProps {
  preSelectedCampaignId?: number;
  onEnrollmentSuccess?: () => void;
}

export default function ManualEnrollmentTab({ preSelectedCampaignId, onEnrollmentSuccess }: ManualEnrollmentTabProps = {}) {
  const [families, setFamilies] = useState<FamilyOption[]>([]);
  const [campaigns, setCampaigns] = useState<CampaignOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [enrollmentResult, setEnrollmentResult] = useState<EnrollmentResult | null>(null);
  const [recentEnrollments, setRecentEnrollments] = useState<RecentEnrollment[]>([]);

  // Form state
  const [familyId, setFamilyId] = useState<number | ''>('');
  const [campaignId, setCampaignId] = useState<number | ''>('');
  const [triggerReason, setTriggerReason] = useState('');
  const [notes, setNotes] = useState('');

  // Validation errors
  const [validationErrors, setValidationErrors] = useState<{
    triggerReason?: string;
    notes?: string;
  }>({});

  useEffect(() => {
    fetchDropdownOptions();
    fetchRecentEnrollments();
  }, []);

  useEffect(() => {
    // Pre-select campaign if provided
    if (preSelectedCampaignId) {
      setCampaignId(preSelectedCampaignId);
    }
  }, [preSelectedCampaignId]);

  const fetchDropdownOptions = async () => {
    setLoading(true);
    try {
      const token = getCurrentAccessToken();
      const headers = { Authorization: `Bearer ${token}` };
      const baseUrl = import.meta.env.VITE_API_URL;

      // Fetch families
      const familiesResponse = await axios.get<{ data: FamilyOption[] }>(
        `${baseUrl}/api/admin/families`,
        {
          params: { page: 1, pageSize: 1000, paginate: false },
          headers,
        }
      );
      setFamilies(familiesResponse.data.data || []);

      // Fetch campaigns
      const campaignsResponse = await axios.get<{
        data: CampaignOption[];
        page: number;
        pageSize: number;
        totalCount: number;
        totalPages: number;
      }>(
        `${baseUrl}/api/admin/email-campaigns`,
        {
          params: { page: 1, pageSize: 100 },
          headers,
        }
      );
      setCampaigns((campaignsResponse.data.data || []).map(c => ({ id: c.id, name: c.name })));
    } catch (err: unknown) {
      console.error('Error fetching dropdown options:', err);
      setError('Failed to load families and campaigns');
    } finally {
      setLoading(false);
    }
  };

  const fetchRecentEnrollments = async () => {
    try {
      // Since there's no dedicated endpoint for recent enrollments, we'll skip this for now
      // The backend would need a GET /api/admin/email-campaigns/enrollments endpoint
      setRecentEnrollments([]);
    } catch (err: unknown) {
      console.error('Error fetching recent enrollments:', err);
    }
  };

  const validateForm = (): boolean => {
    const errors: { triggerReason?: string; notes?: string } = {};

    if (!triggerReason || triggerReason.trim().length < 10) {
      errors.triggerReason = 'Trigger reason must be at least 10 characters';
    } else if (triggerReason.length > 500) {
      errors.triggerReason = 'Trigger reason must not exceed 500 characters';
    }

    if (notes && notes.length > 1000) {
      errors.notes = 'Notes must not exceed 1000 characters';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setEnrollmentResult(null);

    if (!familyId || !campaignId) {
      setError('Please select both a family and a campaign');
      return;
    }

    if (!validateForm()) {
      setError('Please fix validation errors before submitting');
      return;
    }

    setSubmitting(true);
    try {
      const token = getCurrentAccessToken();
      const baseUrl = import.meta.env.VITE_API_URL;

      const response = await axios.post<EnrollmentResult>(
        `${baseUrl}/api/admin/email-campaigns/enrollments`,
        {
          familyId,
          campaignId,
          triggerReason: triggerReason.trim(),
          notes: notes.trim() || null,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setEnrollmentResult(response.data);
      setSuccess(response.data.message);

      // Reset form
      setFamilyId('');
      setCampaignId('');
      setTriggerReason('');
      setNotes('');
      setValidationErrors({});

      // Refresh recent enrollments
      fetchRecentEnrollments();

      // Notify parent component if callback provided (e.g., to refresh campaign metrics)
      if (onEnrollmentSuccess) {
        onEnrollmentSuccess();
      }
    } catch (err: unknown) {
      console.error('Error creating enrollment:', err);
      const message = axios.isAxiosError(err)
        ? (err.response?.data as { message?: string } | undefined)?.message
        : undefined;
      setError(message || 'Failed to create enrollment');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFamilyId('');
    setCampaignId('');
    setTriggerReason('');
    setNotes('');
    setValidationErrors({});
    setError(null);
    setSuccess(null);
    setEnrollmentResult(null);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Enrollment Form */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Enroll Family in Campaign
        </Typography>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth required error={!familyId && submitting}>
                <InputLabel>Family</InputLabel>
                <Select
                  value={familyId}
                  label="Family"
                  onChange={(e) => setFamilyId(e.target.value as number)}
                  disabled={submitting}
                >
                  <MenuItem value="">
                    <em>Select a family</em>
                  </MenuItem>
                  {families.map((family) => (
                    <MenuItem key={family.id} value={family.id}>
                      {family.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth required error={!campaignId && submitting} disabled={!!preSelectedCampaignId}>
                <InputLabel>Campaign</InputLabel>
                <Select
                  value={campaignId}
                  label="Campaign"
                  onChange={(e) => setCampaignId(e.target.value as number)}
                  disabled={submitting || !!preSelectedCampaignId}
                >
                  <MenuItem value="">
                    <em>Select a campaign</em>
                  </MenuItem>
                  {campaigns.map((campaign) => (
                    <MenuItem key={campaign.id} value={campaign.id}>
                      {campaign.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                required
                multiline
                rows={3}
                label="Enrollment Reason (for audit trail)"
                value={triggerReason}
                onChange={(e) => setTriggerReason(e.target.value)}
                disabled={submitting}
                error={!!validationErrors.triggerReason}
                helperText={
                  validationErrors.triggerReason ||
                  `Why are you enrolling this family? (e.g., "Trial user onboarding", "Support request") - ${triggerReason.length}/500 characters (minimum 10)`
                }
                inputProps={{ maxLength: 500 }}
                placeholder="Describe the business reason for this enrollment..."
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Notes (Optional)"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                disabled={submitting}
                error={!!validationErrors.notes}
                helperText={
                  validationErrors.notes ||
                  `${notes.length}/1000 characters`
                }
                inputProps={{ maxLength: 1000 }}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Box display="flex" gap={2}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={submitting}
                >
                  {submitting ? <CircularProgress size={24} /> : 'Enroll Family'}
                </Button>
                <Button
                  type="button"
                  variant="outlined"
                  onClick={resetForm}
                  disabled={submitting}
                >
                  Reset Form
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>

      {/* Scheduled Emails Summary */}
      {enrollmentResult && enrollmentResult.scheduledEmails.length > 0 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Scheduled Emails Created
          </Typography>
          <TableContainer>
            <Table size="small" aria-label="scheduled emails summary">
              <TableHead>
                <TableRow>
                  <TableCell>Email ID</TableCell>
                  <TableCell>Step Name</TableCell>
                  <TableCell>Scheduled Time</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {enrollmentResult.scheduledEmails.map((email, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {email.scheduledEmailId.substring(0, 8)}...
                      </Typography>
                    </TableCell>
                    <TableCell>{email.stepName}</TableCell>
                    <TableCell>{email.scheduledTimeInFamilyTimezone}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Recent Enrollments */}
      {recentEnrollments.length > 0 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Recent Enrollments
          </Typography>
          <TableContainer>
            <Table aria-label="recent enrollments">
              <TableHead>
                <TableRow>
                  <TableCell>Family</TableCell>
                  <TableCell>Campaign</TableCell>
                  <TableCell>Enrolled At</TableCell>
                  <TableCell>Trigger Reason</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Scheduled Emails</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recentEnrollments.map((enrollment) => (
                  <TableRow key={enrollment.id}>
                    <TableCell>{enrollment.familyName}</TableCell>
                    <TableCell>{enrollment.campaignName}</TableCell>
                    <TableCell>
                      {new Date(enrollment.enrolledAt).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap maxWidth={200}>
                        {enrollment.triggerReason}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={enrollment.status} size="small" color="primary" />
                    </TableCell>
                    <TableCell>{enrollment.scheduledEmailsCount}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {recentEnrollments.length === 0 && !loading && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="body2" color="text.secondary" align="center">
            No recent enrollments to display
          </Typography>
        </Paper>
      )}
    </Box>
  );
}
